import {
  HTML_DESCRIPTION_MAX_LENGTH,
  getHTMLPlainTextLength,
} from '@/components/forms/helpers/string-helpers'
import { z } from 'zod'

// This schema describes the manadatory data for creating communities
export const communityCreationSchema = z.object({
  name: z
    .string()
    .min(2, 'Community name must be at least 2 characters.')
    .max(100, 'Community name cannot exceed 100 characters.'),
  languages: z.string(),
  logoImg: z
    .any()
    .optional()
    .refine(
      (file) => (file?.size ? file?.size <= 3000000 : true),
      `Max file size is 3MB.`,
    ),
  headerImg: z
    .any()
    .optional()
    .refine(
      (file) => (file?.size ? file?.size <= 3000000 : true),
      `Max file size is 3MB.`,
    ),
  joinLinkService: z.string().optional(),
  joinLinkUrl: z
    .string()
    .optional()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().url().optional()),

  discordServerId: z.string().nullable().optional(),
  notificationChannelId: z.string().nullable().optional(),
})

// A client-generated community ID makes creation requests safe to retry. The
// database primary key is the final idempotency boundary for concurrent calls.
export const communityCreationRequestSchema = communityCreationSchema.extend({
  communityId: z.string().uuid(),
  logoImgUrl: z.string().url().optional(),
  logoImgS3Meta: z.unknown().optional(),
  headerImgUrl: z.string().url().optional(),
  headerImgS3Meta: z.unknown().optional(),
})

// This schema describes the structure of communties, this can be used for updating communities
export const generalCommunitySchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(2, 'Community name must be at least 2 characters.')
    .max(100, 'Community name cannot exceed 100 characters.')
    .optional(),
  slug: z.string().regex(new RegExp('^[a-z0-9]+(?:-[a-z0-9]+)*$')).optional(),
  descriptionHtml: z
    .string()
    .max(10000, 'Content exceeds the maximum safe HTML size limit')
    .refine(
      (val) =>
        !val || getHTMLPlainTextLength(val) <= HTML_DESCRIPTION_MAX_LENGTH,
      {
        message: `About must contain at most ${HTML_DESCRIPTION_MAX_LENGTH} character(s)`,
      },
    )
    .nullable()
    .optional(),

  logoImg: z
    .any()
    .optional()
    .refine(
      (file) => (file?.size ? file?.size <= 3000000 : true),
      `Max file size is 3MB.`,
    ),
  headerImg: z
    .any()
    .optional()
    .refine(
      (file) => (file?.size ? file?.size <= 3000000 : true),
      `Max file size is 3MB.`,
    ),

  theme: z
    .object({
      primaryColor: z.string().optional(),
      accentColor: z.string().optional(),
    })
    .optional(),
  ownerUserId: z.string().uuid().optional(),
  deleteTs: z.string().datetime().nullable().optional(),
  partneredStatus: z
    .enum(['pending', 'partnered', 'rejected'])
    .nullable()
    .optional(),
  isFeatured: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  isShowcase: z.boolean().optional(),

  joinLinkService: z.string().optional(),
  joinLinkUrl: z
    .string()
    .optional()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().url().optional()),

  languages: z.array(z.string()).optional(),
})
